import { useState } from "react";
import FileUploadForm from "../../../../FileUpload/FileUploadForm";
import { FileUploadState } from "../../../../FileUpload/FileUploadState";
import useCentersOfOperationInitializer from "./useCentersOfOperationInitializer";

// Error type constants for validation
const VALIDATION_ERROR_TYPES = {
    STREET_ADDRESS: "Street Address",
    STREET_ADDRESS_2: "Street Address 2",
    CITY: "City",
    ZIP_CODE: "ZIP Code"
};

export default function CentersOfOperationUpload() {
    const { initializeFromCsv, progress } = useCentersOfOperationInitializer();
    const [fileUploadState, setFileUploadState] = useState(FileUploadState.Upload);
    const [submissionErrorAlertContent, setSubmissionErrorAlertContent] = useState();
    const [showAlert, setShowAlert] = useState(true);

    // Individual alert visibility states
    const [showStreetAddressAlert, setShowStreetAddressAlert] = useState(false);
    const [showCityAlert, setShowCityAlert] = useState(false);
    const [showZipCodeAlert, setShowZipCodeAlert] = useState(false);

    const closeAlert = () => setShowAlert(false);

    const fileUploadParameters = {
        allowedFileExtensions: [".csv"],
        allowInvalidFileTypes: true,
        minFileSize: 0,
        maxFileSize: 25600,
        maxCombinedFileSize: 25600,
        maxFileCount: 1,
        combinedFileSize: 0
    };

    function getValidationErrorTypes(initializeErrors) {
        if (!initializeErrors || initializeErrors.length === 0) {
            return { streetAddress: false, city: false, zipCode: false };
        }

        // Flatten all errors from all rows
        const allErrors = initializeErrors
            .filter(rowError => rowError?.errors)
            .flatMap(rowError => rowError.errors);

        // Check for each error type
        const streetAddressFields = [VALIDATION_ERROR_TYPES.STREET_ADDRESS, VALIDATION_ERROR_TYPES.STREET_ADDRESS_2];
        
        return {
            streetAddress: allErrors.some(error => streetAddressFields.includes(error.field)),
            city: allErrors.some(error => error.field === VALIDATION_ERROR_TYPES.CITY),
            zipCode: allErrors.some(error => error.field === VALIDATION_ERROR_TYPES.ZIP_CODE)
        };
    }

    function onFileUploadSubmit(selectedFiles) {
        initializeFromCsv(selectedFiles[0]).then(errors => {
            if (errors?.invalidFileType) {
                setSubmissionErrorAlertContent(<UnsupportedFileFormatAlert onClose={closeAlert} />);
                setShowAlert(true);
                setFileUploadState(FileUploadState.SubmissionError);
                return;
            }

            if (errors?.missingAgencyInterview) {
                setSubmissionErrorAlertContent(<MissingAgencyInterviewErrorAlert onClose={closeAlert} />);
                setShowAlert(true);
                setFileUploadState(FileUploadState.SubmissionError);
                return;
            }

            const initializeErrors = errors?.initializeErrors;
            if (initializeErrors?.length > 0) {
                const errorTypes = getValidationErrorTypes(initializeErrors);

                // Set individual alerts based on error types
                setShowStreetAddressAlert(errorTypes.streetAddress);
                setShowCityAlert(errorTypes.city);
                setShowZipCodeAlert(errorTypes.zipCode);

                setShowAlert(true);
                setFileUploadState(FileUploadState.SubmissionError);
                return;
            }

            setFileUploadState(FileUploadState.Complete);
        });
    }

    // Build validation error alerts content
    const validationErrorAlerts = (
        <>
            {showStreetAddressAlert && (
                <div className="alert margin-top">
                    <div className="alert--high-importance">
                        <span className="icon-alert"></span>
                        <ul className="alert-list">
                            <StreetAddressValidationErrorAlert onClose={() => setShowStreetAddressAlert(false)} />
                        </ul>
                    </div>
                </div>
            )}
            {showCityAlert && (
                <div className="alert margin-top">
                    <div className="alert--high-importance">
                        <span className="icon-alert"></span>
                        <ul className="alert-list">
                            <CityValidationErrorAlert onClose={() => setShowCityAlert(false)} />
                        </ul>
                    </div>
                </div>
            )}
            {showZipCodeAlert && (
                <div className="alert margin-top">
                    <div className="alert--high-importance">
                        <span className="icon-alert"></span>
                        <ul className="alert-list">
                            <ZipCodeValidationErrorAlert onClose={() => setShowZipCodeAlert(false)} />
                        </ul>
                    </div>
                </div>
            )}
        </>
    );

    // Determine if any validation alerts are showing
    const hasValidationAlerts = showStreetAddressAlert || showCityAlert || showZipCodeAlert;

    return <FileUploadForm trackingId="PrincipalFlow:AgencyOfficeInformation:CentersOfOperationUpload:CentersOfOperationUpload"
        fileUploadState={fileUploadState}
        setFileUploadState={setFileUploadState}
        onFileUploadSubmit={onFileUploadSubmit}
        fileUploadParameters={fileUploadParameters}
        progress={progress}
        displayParameters={{
            submittingTopText: <h3>Submitting...</h3>,
            submittingSubtext: null,
            completeTopText: <h3>File Submitted</h3>,
            errorTopText: <h3>Submission Error</h3>,
            uploadHeader: <h4 className="margin-bottom">File Ready to Submit</h4>,
            submittingHeader: null,
            completeHeader: <h4 className="margin-bottom">Submitted File</h4>,
            errorHeader: <h4 className="margin-bottom">Failed Upload</h4>,
            strokeSeparator: false,
            bottomSubmitButton: true,
            selectedFilesHeaderElement: "h4",
            submitButtonText: "Submit",
            submissionErrorAlertContent: hasValidationAlerts ? null : submissionErrorAlertContent,
            showAlert,
            customAlertContent: hasValidationAlerts ? validationErrorAlerts : null
        }}
    />;
}

function UnsupportedFileFormatAlert({ onClose }) {
    return <li className="alert-content">
        <button data-onboarding-button-id="PrincipalFlow:AgencyOfficeInformation:CentersOfOperationUpload:DismissUnsupportedFormat" aria-label="Close alert" className="icon-close" onClick={onClose}></button>
        <div className="h4">Unsupported File Format</div>
        <p className="margin-bottom-0">Files must be in .csv format.</p>
        <gds-list-wrapper type="numbered-list">
            <gds-simple-list-item type="numbered-list">Delete your file by selecting the trash can icon.</gds-simple-list-item>
            <gds-simple-list-item type="numbered-list">Make sure your file is saved as a .csv</gds-simple-list-item>
            <gds-simple-list-item type="numbered-list">Upload the updated file and resubmit.</gds-simple-list-item>
        </gds-list-wrapper>
    </li>;
}

function StreetAddressValidationErrorAlert({ onClose }) {
    return <li className="alert-content">
        <button data-onboarding-button-id="PrincipalFlow:AgencyOfficeInformation:CentersOfOperationUpload:DismissInvalidStreetAddress" aria-label="Close alert" className="icon-close" onClick={onClose}></button>
        <div className="h4">Entries Must Contain a Valid Street Address</div>
        <p className="margin-bottom-0">Street Address must contain only letters, numbers, and the following special characters: ' - &amp; . / and #.</p>
        <gds-list-wrapper type="numbered-list">
            <gds-simple-list-item type="numbered-list">Delete your file by selecting the trash can icon.</gds-simple-list-item>
            <gds-simple-list-item type="numbered-list">Update your Centers of Operation file to ensure only supported characters are entered in the Street Address fields.</gds-simple-list-item>
            <gds-simple-list-item type="numbered-list">Upload the updated file and resubmit.</gds-simple-list-item>
        </gds-list-wrapper>
    </li>;
}

function CityValidationErrorAlert({ onClose }) {
    return <li className="alert-content">
        <button data-onboarding-button-id="PrincipalFlow:AgencyOfficeInformation:CentersOfOperationUpload:DismissInvalidCity" aria-label="Close alert" className="icon-close" onClick={onClose}></button>
        <div className="h4">Entries Must Contain a Valid City</div>
        <p className="margin-bottom-0">City must contain only letters and hyphens (if applicable).</p>
        <gds-list-wrapper type="numbered-list">
            <gds-simple-list-item type="numbered-list">Delete your file by selecting the trash can icon.</gds-simple-list-item>
            <gds-simple-list-item type="numbered-list">Update your Centers of Operation file to ensure only supported characters are entered in the City field.</gds-simple-list-item>
            <gds-simple-list-item type="numbered-list">Upload the updated file and resubmit.</gds-simple-list-item>
        </gds-list-wrapper>
    </li>;
}

function ZipCodeValidationErrorAlert({ onClose }) {
    return <li className="alert-content">
        <button data-onboarding-button-id="PrincipalFlow:AgencyOfficeInformation:CentersOfOperationUpload:DismissInvalidZipCode" aria-label="Close alert" className="icon-close" onClick={onClose}></button>
        <div className="h4">Entries Must Contain a Valid ZIP Code</div>
        <p className="margin-bottom-0">ZIP Code must be 5 digits and contain only numbers.</p>
        <gds-list-wrapper type="numbered-list">
            <gds-simple-list-item type="numbered-list">Delete your file by selecting the trash can icon.</gds-simple-list-item>
            <gds-simple-list-item type="numbered-list">Update your Centers of Operation file to ensure only supported characters are entered in the ZIP Code field.</gds-simple-list-item>
            <gds-simple-list-item type="numbered-list">Upload the updated file and resubmit.</gds-simple-list-item>
        </gds-list-wrapper>
    </li>;
}

function MissingAgencyInterviewErrorAlert({ onClose }) {
    return <li className="alert-content">
        <button data-onboarding-button-id="PrincipalFlow:AgencyOfficeInformation:CentersOfOperationUpload:DismissMissingAgencyInterview" aria-label="Close alert" className="icon-close" onClick={onClose}></button>
        <div className="h4">Session Error</div>
        <p className="margin-bottom-0">Your session has expired or the agency information is missing.</p>
        <gds-list-wrapper type="numbered-list">
            <gds-simple-list-item type="numbered-list">Please refresh the page and try again.</gds-simple-list-item>
            <gds-simple-list-item type="numbered-list">If the problem persists, please start the onboarding process again.</gds-simple-list-item>
        </gds-list-wrapper>
    </li>;
}

function InitializeErrorAlert({ onClose }) {
    return <li className="alert-content">
        <button data-onboarding-button-id="PrincipalFlow:AgencyOfficeInformation:CentersOfOperationUpload:DismissSubmissionError" aria-label="Close alert" className="icon-close" onClick={onClose}></button>
        <div className="h4">Technical Error</div>
        <p className="margin-bottom-0">The problem initializing some the branches.</p>
    </li>;
}